import React, {useEffect, useState} from 'react';
import {SpinnerContainer} from '@deviceinsight/ng-ui-basic-components';
import './FloorplanWidget.less';
import AssetInfo from './AssetInfo';
import type {ButtonInfo} from './types';
import useFloorplanDeviceColors from './hooks/useFloorplanDeviceColors';
import {useFloorplanSvg} from './hooks/useFloorplanSvg';
import useAssetsFromFloorplanSvg from './hooks/useAssetsFromFloorplanSvg';
import {clearAllBlinks} from '../../common/blinkHelpers';
import {mapLiveDataOnlineBased} from '../../common/onlineBasedLiveDataMapper';
import {DATAPOINT_IS_ONLINE} from '../../../modules/common/consts';
import useButtonTriggers from '../../common/hooks/useButtonTriggers';
import useElementHighlights from './hooks/useElementHighlights';
import useOffClick from './hooks/useOffClick';
import {getAssetGroupFromContext} from './utils/FloorplanWidgetUtils';
import useButtonInteractivity from './hooks/useButtonInteractivity';
import UserStore from '../../../modules/common/UserStore';
import {Asset} from '@deviceinsight/ng-ui-api-client';
import {WidgetSvg} from './hooks/types';
import {WidgetProps} from '@deviceinsight/ng-ui-scale-lib/api';
import {getDigitalTwinDatapointsByAsset, useAssetsSubscription} from '../common/useAssetSubscription';

const FloorplanWidget = (props: WidgetProps) => {
	const {
		config: {
			key,
			settings: {floorplanSvg, uniqueSvgKey, headline}
		},
		editMode,
		context
	} = props;
	const assetGroup = getAssetGroupFromContext(context);
	const user = UserStore.getUser();
	const canPressButton = user && user.authorities.includes('WRITE_BUTTON_EVENT');
	const [selectedAssetId, setSelectedAssetId] = useState<number | void>();
	const [assetsCached, setAssetsCached] = useState<Array<Asset>>([]);
	const [buttonInfo, setButtonInfo] = useState<ButtonInfo | void>(undefined);
	const [loading, setLoading] = useState<boolean>(false);
	const [svgRootObj, setSvgRootObj] = useState<WidgetSvg>();
	useFloorplanSvg(floorplanSvg, uniqueSvgKey, key, setSvgRootObj);
	useAssetsFromFloorplanSvg(svgRootObj, setAssetsCached, assetGroup, setLoading);
	const liveData = useAssetsSubscription(assetsCached, getDigitalTwinDatapointsByAsset, mapLiveDataOnlineBased);
	useFloorplanDeviceColors(svgRootObj, liveData, editMode, key);
	useButtonInteractivity(svgRootObj, canPressButton, setButtonInfo);

	// we want to clear all blinks when components get unmounted....
	useEffect(() => {
		return () => {
			clearAllBlinks();
		};
	}, [svgRootObj, editMode]);
	useButtonTriggers(
		buttonInfo ? assetsCached.find((asset) => asset.id === parseInt(buttonInfo.assetId)) : undefined,
		buttonInfo && liveData[buttonInfo.assetId] ? liveData[buttonInfo.assetId][DATAPOINT_IS_ONLINE] : false,
		buttonInfo ?? undefined,
		setButtonInfo
	);
	useElementHighlights(svgRootObj, setSelectedAssetId, selectedAssetId ?? undefined);
	useOffClick(svgRootObj, () => setSelectedAssetId(), Array.from(document.querySelectorAll('.floorplan-asset-info')));
	const containerId = `svg-floorplan-${key}`;
	return (
		<SpinnerContainer show={loading} size={50}>
			<div className="floorplan-widget dash-box">
				{headline && (
					<div className="dash-box-head-container">
						<div className="dash-box-head">{headline}</div>
					</div>
				)}
				<AssetInfo
					asset={selectedAssetId ? assetsCached.find((asset) => asset.id === selectedAssetId) : undefined}
					liveData={selectedAssetId ? liveData[selectedAssetId] : undefined}
				/>
				<div className="floorplan-widget-content">
					<div
						id={containerId}
						style={{
							width: '100%',
							height: '100%'
						}}
						className="floorplan-widget-container"
					/>
				</div>
			</div>
		</SpinnerContainer>
	);
};

export default FloorplanWidget;
